import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Switch } from "@headlessui/react";

const BoatDetailsPage = ({ onNext, onBack, formData: initialFormData }) => {
  const navigate = useNavigate();

  // Form state, initialize with previous step data if available
  const [formData, setFormData] = useState({
    boatType: initialFormData?.boatType || "",
    boatLength: initialFormData?.boatLength || "",
    countryFlag: initialFormData?.countryFlag || "",
    boatLocation: initialFormData?.boatLocation || "",
    boatRegion: initialFormData?.boatRegion || [],
    boatBrand: initialFormData?.boatBrand || "",
    boatYear: initialFormData?.boatYear || "",
    motorCount: initialFormData?.motorCount || 1,
    motorPower: initialFormData?.motorPower || [""],
    motorSerial: initialFormData?.motorSerial || [""],
    motorType: initialFormData?.motorType || [""], // Nieuw: per motor
    fuelType: initialFormData?.fuelType || [""], // Nieuw: per motor
  });

  const [errors, setErrors] = useState({});

  const regions = ["Benelux-Noord Europa", "Middellandse Zee"];
  const motorTypes = ["Inboard", "Outboard"];
  const fuelTypes = ["Benzine", "Diesel", "Elektrisch"];

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Als de naam "boatLength" is, controleer en begrens de waarde op maximaal 100
    if (name === "boatLength") {
      const length = Math.min(Number(value), 100); // Maximale waarde is 100
      setFormData({ ...formData, [name]: length });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    setErrors({ ...errors, [name]: "" });
  };

  const handleMotorChange = (index, value) => {
    const updatedMotorPower = [...formData.motorPower];
    updatedMotorPower[index] = value;
    setFormData({ ...formData, motorPower: updatedMotorPower });

    const motorErrors = { ...errors.motorPower };
    motorErrors[index] = "";
    setErrors({ ...errors, motorPower: motorErrors });
  };

  const handleMotorenChange = (e) => {
    let value = Number(e.target.value);
    let newErrors = { ...errors };

    // Validatie: Minimaal 1 motor voor motorboot, RIB en jetski
    if (
      ["Motorboot", "RIB", "Jetski"].includes(formData.boatType) &&
      value < 1
    ) {
      newErrors.motorCount = "Dit vaartuig moet minstens 1 motor hebben.";
    } else if (value > 3) {
      newErrors.motorCount = "Je kunt maximaal 3 motoren toevoegen.";
    } else {
      delete newErrors.motorCount; // Verwijder foutmelding als de waarde correct is
    }

    setErrors(newErrors);

    if (!newErrors.motorCount) {
      setFormData({
        ...formData,
        motorCount: value,
        motorPower: Array(value).fill(""),
        motorSerial: Array(value).fill(""),
        motorType: Array(value).fill(""),
        fuelType: Array(value).fill(""),
      });
    }
  };

  const handleMotorDetailChange = (index, field, value) => {
    setFormData((prevState) => {
      const updatedArray = [...prevState[field]];
      updatedArray[index] = value;
      return { ...prevState, [field]: updatedArray };
    });
  };

  const handleRegionChange = (e) => {
    const value = e.target.value;
    const updatedRegions = e.target.checked
      ? [...formData.boatRegion, value]
      : formData.boatRegion.filter((region) => region !== value);
    setFormData({ ...formData, boatRegion: updatedRegions });
  };

  const handleNext = () => {
    const newErrors = {};

    if (!formData.boatType) newErrors.boatType = "Dit veld is verplicht.";
    if (!formData.boatLength) newErrors.boatLength = "Dit veld is verplicht.";
    if (!formData.countryFlag) newErrors.countryFlag = "Dit veld is verplicht.";
    if (!formData.boatLocation)
      newErrors.boatLocation = "Dit veld is verplicht.";
    if (!formData.boatBrand) newErrors.boatBrand = "Dit veld is verplicht.";
    if (!formData.boatYear) newErrors.boatYear = "Dit veld is verplicht.";
    if (!formData.motorPower.every((power) => power))
      newErrors.motorPower = formData.motorPower.map((power) =>
        !power ? "Dit veld is verplicht." : ""
      );
    if (!formData.motorSerial.every((serial) => serial))
      newErrors.motorSerial = formData.motorSerial.map((serial) =>
        !serial ? "Dit veld is verplicht." : ""
      );
    if (!formData.motorType.every((type) => type))
      newErrors.motorType = formData.motorType.map((type) =>
        !type ? "Selecteer een motortype." : ""
      );
    if (!formData.fuelType.every((fuel) => fuel))
      newErrors.fuelType = formData.fuelType.map((fuel) =>
        !fuel ? "Selecteer een brandstoftype." : ""
      );

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext(formData);
    }
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-semibold mb-6 text-left text-[#1a2a6c]">
          Over jouw <span className="text-[#ff6f61]">{formData.boatType}</span>
        </h1>
        <p className="text-lg font-medium text-[#1a2a6c] mb-6">
          Bezorg ons enkele details over jouw motorboot.
        </p>

        <form className="space-y-8">
          {/* Boot Type */}
          <div>
            <label
              htmlFor="boatType"
              className="block text-sm font-semibold text-[#1a2a6c]"
            >
              Boottype
            </label>
            <input
              disabled="true"
              id="boatType"
              type="text"
              name="boatType"
              value={formData.boatType}
              onChange={handleChange}
              required
              className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
            />
            {errors.boatType && (
              <p className="text-sm text-red-600 mt-1">{errors.boatType}</p>
            )}
          </div>

          {/* Bootlengte en Land */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="boatLength"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Bootlengte (in meters)
              </label>
              <input
                id="boatLength"
                type="number"
                max="100"
                name="boatLength"
                value={formData.boatLength}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.boatLength && (
                <p className="text-sm text-red-600 mt-1">{errors.boatLength}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="countryFlag"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Land van registratie
              </label>
              <select
                id="countryFlag"
                name="countryFlag"
                value={formData.countryFlag}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              >
                <option value="">Selecteer een land</option>
                <option value="BE">België</option>
                <option value="NL">Nederland</option>
                <option value="FR">Frankrijk</option>
                <option value="ES">Spanje</option>
                <option value="IT">Italië</option>
                <option value="LU">Luxemburg</option>
                <option value="none">Geen registratie nodig</option>
              </select>
              {errors.countryFlag && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.countryFlag}
                </p>
              )}
            </div>
          </div>

          {/* Ligplaats */}
          <div>
            <label
              htmlFor="boatLocation"
              className="block text-sm font-semibold text-[#1a2a6c]"
            >
              Ligplaats
            </label>
            <input
              id="boatLocation"
              type="text"
              name="boatLocation"
              value={formData.boatLocation}
              onChange={handleChange}
              required
              className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
            />
            {errors.boatLocation && (
              <p className="text-sm text-red-600 mt-1">{errors.boatLocation}</p>
            )}
          </div>

          {/* Vaargebied */}
          <div>
            <label className="block text-sm font-semibold text-[#1a2a6c] mb-2">
              Vaargebied
            </label>
            <div className="space-y-4">
              {regions.map((region) => (
                <div key={region} className="flex items-center justify-between">
                  <label className="text-sm text-gray-700">{region}</label>
                  <Switch
                    checked={formData.boatRegion.includes(region)}
                    onChange={(checked) => {
                      const updatedRegions = checked
                        ? [...formData.boatRegion, region]
                        : formData.boatRegion.filter((r) => r !== region);
                      setFormData({ ...formData, boatRegion: updatedRegions });
                    }}
                    className={`${
                      formData.boatRegion.includes(region)
                        ? "bg-[#1a2a6c]"
                        : "bg-gray-200"
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                  >
                    <span
                      className={`${
                        formData.boatRegion.includes(region)
                          ? "translate-x-6 bg-[#ff6f61]" // Actieve kleur
                          : "translate-x-1 bg-white" // Inactieve kleur
                      } inline-block h-4 w-4 transform rounded-full transition`}
                    />
                  </Switch>
                </div>
              ))}
            </div>
          </div>

          {/* Boot Merk en Bouwjaar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="boatBrand"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Merk
              </label>
              <input
                id="boatBrand"
                type="text"
                name="boatBrand"
                value={formData.boatBrand}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.boatBrand && (
                <p className="text-sm text-red-600 mt-1">{errors.boatBrand}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="boatYear"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Bouwjaar
              </label>
              <input
                id="boatYear"
                type="number"
                name="boatYear"
                value={formData.boatYear}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.boatYear && (
                <p className="text-sm text-red-600 mt-1">{errors.boatYear}</p>
              )}
            </div>
          </div>

          {/* Motoren */}
          <div>
            <label
              htmlFor="motorCount"
              className="block text-sm font-semibold text-[#1a2a6c]"
            >
              Aantal motoren
            </label>
            <input
              id="motorCount"
              type="number"
              name="motorCount"
              value={formData.motorCount}
              onChange={handleMotorenChange}
              required
              className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
            />
            {errors.motorCount && (
              <p className="text-sm text-red-600 mt-1">{errors.motorCount}</p>
            )}
          </div>

          {formData.motorPower.map((power, index) => (
            <div key={index} className="space-y-4">
              <div>
                <label
                  htmlFor={`motorPower${index}`}
                  className="block text-sm font-semibold text-[#1a2a6c]"
                >
                  Motor {index + 1} vermogen (PK/kW)
                </label>
                <input
                  id={`motorPower${index}`}
                  type="text"
                  value={power}
                  onChange={(e) => handleMotorChange(index, e.target.value)}
                  required
                  className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                />
                {errors.motorPower && errors.motorPower[index] && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.motorPower[index]}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor={`motorSerial${index}`}
                  className="block text-sm font-semibold text-[#1a2a6c]"
                >
                  Motor {index + 1} serienummer
                </label>
                <input
                  id={`motorSerial${index}`}
                  type="text"
                  value={
                    formData.motorSerial ? formData.motorSerial[index] : ""
                  }
                  onChange={(e) => {
                    const updatedMotorSerial = [
                      ...(formData.motorSerial || []),
                    ];
                    updatedMotorSerial[index] = e.target.value;
                    setFormData({
                      ...formData,
                      motorSerial: updatedMotorSerial,
                    });
                  }}
                  required
                  className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                />
                {errors.motorSerial && errors.motorSerial[index] && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.motorSerial[index]}
                  </p>
                )}
              </div>

              {/* Type motor */}
              <div>
                <label className="block text-sm font-semibold text-[#1a2a6c]">
                  Type motor
                </label>
                <select
                  value={formData.motorType[index]}
                  onChange={(e) =>
                    handleMotorDetailChange(index, "motorType", e.target.value)
                  }
                  required
                  className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md"
                >
                  <option value="">Selecteer motortype</option>
                  {motorTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.motorType && errors.motorType[index] && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.motorType[index]}
                  </p>
                )}
              </div>

              {/* Brandstof */}
              <div>
                <label className="block text-sm font-semibold text-[#1a2a6c]">
                  Brandstof
                </label>
                <select
                  value={formData.fuelType[index]}
                  onChange={(e) =>
                    handleMotorDetailChange(index, "fuelType", e.target.value)
                  }
                  required
                  className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md"
                >
                  <option value="">Selecteer brandstof</option>
                  {fuelTypes.map((fuel) => (
                    <option key={fuel} value={fuel}>
                      {fuel}
                    </option>
                  ))}
                </select>
                {errors.fuelType && errors.fuelType[index] && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.fuelType[index]}
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* Navigation */}
          <div className="flex justify-between mt-6 gap-4">
            <button
              type="button"
              onClick={onBack}
              className="w-auto py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md text-sm font-medium hover:bg-[#142057]"
            >
              Terug
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="w-auto py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md text-sm font-medium hover:bg-[#142057]"
            >
              Volgende
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BoatDetailsPage;
